import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './UserEditPage.css';

const API    = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/admin/users`;
const API_AUTH = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/auth`;

const ROLES = ['ADMIN', 'USER'];

interface UserDetail {
  userId:      number;
  username:    string;
  email:       string;
  firstName:   string;
  lastName:    string;
  dateOfBirth: string | null;
  role:        string;
  active:      boolean;
  createdDate: string | null;
  lastLogin:   string | null;
  loginCount:  number;
}

function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function UserEditPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate   = useNavigate();
  const isNew      = !userId;

  const [firstName,   setFirstName]   = useState('');
  const [lastName,    setLastName]    = useState('');
  const [username,    setUsername]    = useState('');
  const [email,       setEmail]       = useState('');
  const [dob,         setDob]         = useState(''); // YYYY-MM-DD for input[type=date]
  const [role,        setRole]        = useState('USER');
  const [active,      setActive]      = useState(true);
  const [password,    setPassword]    = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [existing,    setExisting]    = useState<UserDetail | null>(null);
  const [loading,     setLoading]     = useState(!isNew);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  // Username availability (create only)
  const [usernameFeedback, setUsernameFeedback] = useState<{available: boolean; reason: string} | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const usernameDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Email availability
  const [emailFeedback, setEmailFeedback] = useState<{available: boolean; reason: string} | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const emailDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isNew && userId) {
      axios.get(API).then(res => {
        const u = res.data.find((x: UserDetail) => x.userId === parseInt(userId));
        if (u) {
          setExisting(u);
          setFirstName(u.firstName || '');
          setLastName(u.lastName || '');
          setUsername(u.username || '');
          setEmail(u.email || '');
          setDob(u.dateOfBirth || '');
          setRole(u.role || 'USER');
          setActive(u.active);
        }
      }).catch(() => setError('Failed to load user'))
        .finally(() => setLoading(false));
    }
  }, [userId, isNew]);

  // Debounced username check (create only)
  useEffect(() => {
    if (!isNew || username.length < 6) { setUsernameFeedback(null); return; }
    if (usernameDebounce.current) clearTimeout(usernameDebounce.current);
    usernameDebounce.current = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const res = await axios.get(`${API_AUTH}/check-username?username=${encodeURIComponent(username)}`);
        setUsernameFeedback(res.data);
      } catch { setUsernameFeedback(null); }
      finally { setCheckingUsername(false); }
    }, 800);
  }, [username, isNew]);

  // Debounced email check
  useEffect(() => {
    if (!email.includes('@')) { setEmailFeedback(null); return; }
    if (emailDebounce.current) clearTimeout(emailDebounce.current);
    emailDebounce.current = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const excludeParam = !isNew && userId ? `&excludeUserId=${userId}` : '';
        const res = await axios.get(`${API_AUTH}/check-email?email=${encodeURIComponent(email)}${excludeParam}`);
        setEmailFeedback(res.data);
      } catch { setEmailFeedback(null); }
      finally { setCheckingEmail(false); }
    }, 800);
  }, [email, isNew, userId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isNew && usernameFeedback && !usernameFeedback.available) {
      setError('Please choose a different username.'); return;
    }
    if (emailFeedback && !emailFeedback.available) {
      setError('That email address is already in use.'); return;
    }
    if (isNew && password !== confirmPw) {
      setError('Passwords do not match.'); return;
    }
    if (isNew && password.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }
    if (!isNew && password && password.length < 8) {
      setError('Password must be at least 8 characters.'); return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await axios.post(API, {
          firstName, lastName, username, email,
          dateOfBirth: dob, role, password,
        });
      } else {
        await axios.put(`${API}/${userId}`, {
          firstName, lastName, email,
          dateOfBirth: dob, role, active,
          ...(password ? { password } : {}),
        });
      }
      navigate('/admin', { state: { tab: 'users', success: isNew ? 'User created' : 'User updated' } });
    } catch (e: any) {
      setError(e.response?.data?.error || (isNew ? 'Failed to create user' : 'Failed to update user'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="user-edit-loading">Loading…</div>;

  return (
    <div className="user-edit-page">
      <div className="user-edit-card">
        <div className="user-edit-header">
          <button className="user-edit-back" onClick={() => navigate('/admin', { state: { tab: 'users' } })}>
            ← Back to Users
          </button>
          <h1 className="user-edit-title">
            {isNew ? 'Create User' : `Edit User — ${existing?.username}`}
          </h1>
        </div>

        {error && <div className="user-edit-error">{error}</div>}

        <form className="user-edit-form" onSubmit={handleSubmit} autoComplete="off">

          {/* Read-only stats — edit mode only */}
          {!isNew && existing && (
            <div className="user-edit-stats">
              <div className="user-stat">
                <span className="user-stat-label">Joined</span>
                <span className="user-stat-value">{fmtDate(existing.createdDate)}</span>
              </div>
              <div className="user-stat">
                <span className="user-stat-label">Last Login</span>
                <span className="user-stat-value">{fmtDate(existing.lastLogin)}</span>
              </div>
              <div className="user-stat">
                <span className="user-stat-label">Total Logins</span>
                <span className="user-stat-value">{existing.loginCount || 0}</span>
              </div>
            </div>
          )}

          <div className="user-edit-grid">
            <div className="user-edit-field">
              <label>First Name</label>
              <input type="text" value={firstName}
                     onChange={e => setFirstName(e.target.value)}
                     placeholder="First name" autoComplete="off" />
            </div>
            <div className="user-edit-field">
              <label>Last Name</label>
              <input type="text" value={lastName}
                     onChange={e => setLastName(e.target.value)}
                     placeholder="Last name" autoComplete="off" />
            </div>

            {/* Username — editable on create, readonly on edit */}
            <div className="user-edit-field">
              <label>
                Username
                {!isNew && <span className="user-edit-readonly"> (cannot change)</span>}
              </label>
              <div className="user-edit-input-wrap">
                <input type="text" value={username}
                       onChange={e => isNew && setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                       placeholder="Username"
                       autoComplete="off"
                       readOnly={!isNew}
                       minLength={isNew ? 6 : undefined}
                       maxLength={20}
                       className={!isNew ? 'readonly' : ''} />
                {isNew && checkingUsername && <span className="user-edit-check checking">⟳</span>}
                {isNew && !checkingUsername && usernameFeedback && (
                  <span className={`user-edit-check ${usernameFeedback.available ? 'ok' : 'fail'}`}>
                    {usernameFeedback.available ? '✓' : '✗'}
                  </span>
                )}
              </div>
              {isNew && usernameFeedback && !usernameFeedback.available && (
                <span className="user-edit-field-error">{usernameFeedback.reason}</span>
              )}
              {isNew && usernameFeedback?.available && (
                <span className="user-edit-field-ok">Username is available</span>
              )}
            </div>

            {/* Email with uniqueness check */}
            <div className="user-edit-field">
              <label>Email Address</label>
              <div className="user-edit-input-wrap">
                <input type="email" value={email}
                       onChange={e => setEmail(e.target.value)}
                       placeholder="email@example.com"
                       autoComplete="off" required />
                {checkingEmail && <span className="user-edit-check checking">⟳</span>}
                {!checkingEmail && emailFeedback && (
                  <span className={`user-edit-check ${emailFeedback.available ? 'ok' : 'fail'}`}>
                    {emailFeedback.available ? '✓' : '✗'}
                  </span>
                )}
              </div>
              {emailFeedback && !emailFeedback.available && (
                <span className="user-edit-field-error">{emailFeedback.reason}</span>
              )}
            </div>

            {/* DOB — native date picker */}
            <div className="user-edit-field">
              <label>Date of Birth</label>
              <input type="date" value={dob}
                     onChange={e => setDob(e.target.value)}
                     autoComplete="off" />
            </div>

            <div className="user-edit-field">
              <label>Role</label>
              <select value={role} onChange={e => setRole(e.target.value)}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {/* Access toggle — edit only */}
            {!isNew && (
              <div className="user-edit-field user-edit-field-full">
                <label>Account Access</label>
                <label className="user-edit-toggle">
                  <input type="checkbox" checked={active}
                         onChange={e => setActive(e.target.checked)} />
                  <span className={`user-edit-toggle-label ${active ? 'active' : 'revoked'}`}>
                    {active ? '✓ Active — user can sign in' : '✗ Revoked — user cannot sign in'}
                  </span>
                </label>
              </div>
            )}

            <div className="user-edit-field">
              <label>
                {isNew ? 'Password' : 'New Password'}
                {!isNew && <span className="user-edit-hint"> (leave blank to keep current)</span>}
              </label>
              <input type="password" value={password}
                     onChange={e => setPassword(e.target.value)}
                     placeholder={isNew ? 'Min 8 characters' : 'Leave blank to keep current'}
                     autoComplete="new-password"
                     required={isNew} />
            </div>

            {(isNew || password) && (
              <div className="user-edit-field">
                <label>Confirm Password</label>
                <div className="user-edit-input-wrap">
                  <input type="password" value={confirmPw}
                         onChange={e => setConfirmPw(e.target.value)}
                         placeholder="Repeat password"
                         autoComplete="new-password"
                         required={isNew} />
                  {confirmPw && (
                    <span className={`user-edit-check ${confirmPw === password ? 'ok' : 'fail'}`}>
                      {confirmPw === password ? '✓' : '✗'}
                    </span>
                  )}
                </div>
                {confirmPw && confirmPw !== password && (
                  <span className="user-edit-field-error">Passwords do not match</span>
                )}
              </div>
            )}
          </div>

          <div className="user-edit-actions">
            <button type="button" className="user-edit-cancel"
                    onClick={() => navigate('/admin', { state: { tab: 'users' } })}>
              Cancel
            </button>
            <button type="submit" className="user-edit-save" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create User' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
